export type SystemSortOption =
  | 'recommended'
  | 'price-asc'
  | 'operating-cost-asc'
  | 'cost-of-ownership-asc'
  | 'heating-desc'
  | 'cooling-desc'
  | 'heating-cooling-desc';

export type SystemSortMetrics = {
  price: number | null;
  operatingCost: number | null;
  // Purchase price plus the server's horizon of operating cost; only ducted
  // rows carry it (the server stamps it on ranked rows).
  costOfOwnership: number | null;
  heating: number | null;
  cooling: number | null;
  heatingCooling: number | null;
};

type SystemSortMetricKey = keyof SystemSortMetrics;

type SystemSortRule = {
  metric: SystemSortMetricKey;
  direction: 'asc' | 'desc';
};

export const DEFAULT_SYSTEM_SORT: SystemSortOption = 'recommended';

export const SYSTEM_SORT_LABELS: Record<SystemSortOption, string> = {
  recommended: 'Recommended',
  'price-asc': 'Price: Low to High',
  'operating-cost-asc': 'Operating Cost: Low to High',
  'cost-of-ownership-asc': 'Cost of Ownership (10 yr): Low to High',
  'heating-desc': 'Heating Coverage: High to Low',
  'cooling-desc': 'Cooling Coverage: High to Low',
  'heating-cooling-desc': 'Combined Coverage: High to Low'
};

// Cost of ownership is a ducted figure (the server prices the ducted pool
// over its horizon); the ductless gallery has no equivalent, so the option
// is offered on the ducted path only.
const DUCTED_ONLY_SORT_OPTIONS: ReadonlySet<SystemSortOption> = new Set([
  'cost-of-ownership-asc'
]);

export const systemSortOptions: { value: SystemSortOption; label: string }[] = (
  Object.entries(SYSTEM_SORT_LABELS) as [SystemSortOption, string][]
).map(([value, label]) => ({ value, label }));

export const systemSortOptionsFor = (
  ducted: boolean
): { value: SystemSortOption; label: string }[] =>
  ducted
    ? systemSortOptions
    : systemSortOptions.filter(
        (option) => !DUCTED_ONLY_SORT_OPTIONS.has(option.value)
      );

const SYSTEM_SORT_RULES: Record<
  Exclude<SystemSortOption, 'recommended'>,
  SystemSortRule
> = {
  'price-asc': { metric: 'price', direction: 'asc' },
  'operating-cost-asc': { metric: 'operatingCost', direction: 'asc' },
  'cost-of-ownership-asc': { metric: 'costOfOwnership', direction: 'asc' },
  'heating-desc': { metric: 'heating', direction: 'desc' },
  'cooling-desc': { metric: 'cooling', direction: 'desc' },
  'heating-cooling-desc': { metric: 'heatingCooling', direction: 'desc' }
};

export const isSystemSortOption = (value: unknown): value is SystemSortOption =>
  typeof value === 'string' && value in SYSTEM_SORT_LABELS;

export const combinedCoverage = (
  heating: number | null,
  cooling: number | null
): number | null =>
  heating === null && cooling === null ? null : (heating ?? 0) + (cooling ?? 0);

const compareMetricValues = (
  a: number | null,
  b: number | null,
  direction: 'asc' | 'desc'
): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return direction === 'asc' ? a - b : b - a;
};

export function sortBySystemSortOption<T>(
  items: readonly T[],
  option: SystemSortOption,
  metricsOf: (item: T) => SystemSortMetrics
): T[] {
  if (option === 'recommended') return [...items];
  const { metric, direction } = SYSTEM_SORT_RULES[option];
  const keyed = items.map((item, index) => ({
    item,
    index,
    value: metricsOf(item)[metric]
  }));
  keyed.sort(
    (a, b) =>
      compareMetricValues(a.value, b.value, direction) || a.index - b.index
  );
  return keyed.map(({ item }) => item);
}
